/**
 * WalletBalanceCard — F-009.3.
 *
 * Header del wallet: saldo corrente in evidenza, fido e disponibilità effettiva,
 * banner di stato (OverdraftBanner) e CTA "Ricarica" prominente.
 *
 * Read-only: i valori arrivano dal summary (cents come stringa). La disponibilità
 * può essere negativa (sconfinamento entro/oltre fido) → mostrata in rosso.
 */
import { Wallet } from 'lucide-react';
import type { WalletSummaryResponse } from '@app/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { centsToEuro, centsToNumber } from '../lib/format';
import { OverdraftBanner } from './OverdraftBanner';

interface WalletBalanceCardProps {
  summary: WalletSummaryResponse;
  onRicarica: () => void;
}

export function WalletBalanceCard({ summary, onRicarica }: WalletBalanceCardProps) {
  const availableNegative = centsToNumber(summary.availableCents) < 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" aria-hidden="true" />
              Il tuo wallet
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Saldo prepagato per la registrazione delle pratiche.
            </p>
          </div>
          <Button onClick={onRicarica} disabled={summary.walletBlocked}>
            Ricarica
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <OverdraftBanner summary={summary} />

        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Saldo corrente</p>
          <p className="mt-1 text-4xl font-semibold tabular-nums" data-testid="wallet-balance">
            {centsToEuro(summary.balanceCents)}
          </p>
        </div>

        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              Fido disponibile
            </dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums">
              {centsToEuro(summary.fidoLimitCents)}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              Disponibilità effettiva
            </dt>
            <dd
              className={[
                'mt-1 text-lg font-semibold tabular-nums',
                availableNegative ? 'text-destructive' : '',
              ].join(' ')}
            >
              {centsToEuro(summary.availableCents)}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}
